import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Avatar from './Avatar';
import axios from 'axios';

const typeColors = {
  analyse: { bg: '#EBF4FF', color: '#2B6CB0', label: 'Analyse' },
  projet: { bg: '#F0FFF4', color: '#276749', label: 'Projet' },
  decouverte: { bg: '#FFF5F5', color: '#9B2C2C', label: 'Découverte' },
  post: { bg: '#F7F9FC', color: '#4A5568', label: 'Post' }
};

export default function PostCard({ post, onUpdate }) {
  const { token, API, user } = useAuth();
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [showComments, setShowComments] = useState(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState(post.comments || []);
  const [posting, setPosting] = useState(false);
  const [hovered, setHovered] = useState(false);

  const type = typeColors[post.post_type] || typeColors.post;
  const date = new Date(post.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  const mediaUrl = post.media_url ? `https://social-agora.com${post.media_url}` : null;

  const handleDelete = async () => {
    if (!window.confirm("Supprimer ce post ?")) return;
    try { await axios.delete(`${API}/posts/${post.id}`, { headers: { Authorization: `Bearer ${token}` } }); if (onUpdate) onUpdate(); }
    catch (err) { console.error(err); }
  };

  const handleLike = async () => {
    if (!token) return;
    try {
      const res = await axios.post(`${API}/posts/${post.id}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setLiked(res.data.liked);
      setLikesCount(res.data.likes_count);
    } catch (err) { console.error(err); }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/posts/${post.id}/comment`, { content: comment }, { headers: { Authorization: `Bearer ${token}` } });
      setComments([...comments, res.data]);
      setComment('');
      if (onUpdate) onUpdate();
    } catch (err) { console.error(err); }
    finally { setPosting(false); }
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: '#fff', borderRadius: '18px', border: `1px solid ${hovered ? 'var(--blue-border)' : 'var(--border)'}`, marginBottom: '16px', transform: hovered ? 'translateY(-3px)' : 'translateY(0)', boxShadow: hovered ? 'var(--shadow-hover)' : 'var(--shadow)', transition: 'all .3s ease', overflow: 'hidden' }}
    >
      <div style={{ padding: '24px 26px 0' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
          <Link to={`/profile/${post.author.username}`}>
            <Avatar user={post.author} size={42} fontSize={14} />
          </Link>
          <div style={{ flex: 1 }}>
            <Link to={`/profile/${post.author.username}`} style={{ fontSize: '14px', fontWeight: 700, color: 'var(--dark)', transition: 'color .2s' }}
              onMouseEnter={e => e.target.style.color = 'var(--blue)'}
              onMouseLeave={e => e.target.style.color = 'var(--dark)'}
            >{post.author.username}</Link>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{date}</div>
          </div>
          <span style={{ background: type.bg, color: type.color, fontSize: '11px', padding: '4px 12px', borderRadius: '20px', fontWeight: 600 }}>{type.label}</span>
        </div>

        {/* Content */}
        {post.content && (
          <p style={{ fontSize: '15px', color: '#2D3748', lineHeight: 1.8, margin: '0 0 14px', wordBreak: 'break-word' }}>
            {post.content}
          </p>
        )}
      </div>

      {/* Media */}
      {mediaUrl && (
        <div style={{ marginBottom: '0' }}>
          {post.media_type === 'video' ? (
            <video src={mediaUrl} controls style={{ width: '100%', maxHeight: '400px', objectFit: 'cover', display: 'block' }} />
          ) : (
            <img src={mediaUrl} alt="media" style={{ width: '100%', maxHeight: '400px', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
              onClick={() => window.open(mediaUrl, '_blank')}
            />
          )}
        </div>
      )}

      <div style={{ padding: '0 26px 24px' }}>
        {/* Project link */}
        {post.project_id && post.project_title && (
          <Link to={`/projects/${post.project_id}`} style={{ textDecoration: 'none', display: 'block', marginTop: '14px' }}>
            <div style={{ background: 'var(--blue-bg)', border: '1px solid var(--blue-border)', borderRadius: '12px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all .2s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--blue-bg)'}
            >
              <span style={{ fontSize: '20px' }}>{post.project_emoji || '🚀'}</span>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--blue)', fontWeight: 600, letterSpacing: '0.5px' }}>PROJET LIÉ</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--blue)' }}>{post.project_title}</div>
              </div>
              <span style={{ marginLeft: 'auto', fontSize: '13px', color: 'var(--blue)', fontWeight: 600 }}>Voir →</span>
            </div>
          </Link>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', paddingTop: '14px', borderTop: '1px solid #EEF4FB', marginTop: post.project_id ? '14px' : '14px' }}>
          <button onClick={handleLike} style={{ background: liked ? '#FFF5F5' : 'transparent', border: 'none', cursor: token ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: liked ? '#E53E3E' : 'var(--muted)', padding: '6px 12px', borderRadius: '20px', transition: 'all .2s' }}
            onMouseEnter={e => { if (token) e.currentTarget.style.background = liked ? '#FED7D7' : 'var(--bg)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = liked ? '#FFF5F5' : 'transparent'; }}
          >
            <span style={{ fontSize: '16px' }}>{liked ? '♥' : '♡'}</span>
            {likesCount}
          </button>

          <button onClick={() => setShowComments(!showComments)} style={{ background: showComments ? 'var(--blue-bg)' : 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: showComments ? 'var(--blue)' : 'var(--muted)', padding: '6px 12px', borderRadius: '20px', transition: 'all .2s' }}
            onMouseEnter={e => { if (!showComments) e.currentTarget.style.background = 'var(--bg)'; }}
            onMouseLeave={e => { if (!showComments) e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ fontSize: '15px' }}>💬</span>
            {comments.length} {comments.length === 1 ? 'commentaire' : 'commentaires'}
          </button>
        {user && user.username === post.author.username && (
          <button onClick={handleDelete} style={{ background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600, color: "var(--muted)", padding: "6px 12px", borderRadius: "20px", marginLeft: "auto" }}
            onMouseEnter={e => e.currentTarget.style.color = "#E53E3E"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--muted)"}
          >🗑️ Supprimer</button>
        )}
        </div>
        {/* Comments */}
        {showComments && (
          <div style={{ marginTop: '18px' }}>
            {comments.length > 0 && (
              <div style={{ marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {comments.map(c => (
                  <div key={c.id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <Avatar user={c.author} size={30} fontSize={10} />
                    <div style={{ background: 'var(--bg)', borderRadius: '12px', padding: '10px 14px', flex: 1, border: '1px solid var(--border)' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--blue)' }}>{c.author.username}</span>
                      <span style={{ fontSize: '13px', color: '#2D3748', marginLeft: '8px' }}>{c.content}</span>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                        {new Date(c.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {user && (
              <form onSubmit={handleComment} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Avatar user={user} size={30} fontSize={10} />
                <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Écris un commentaire..."
                  style={{ flex: 1, border: '1.5px solid var(--blue-border)', borderRadius: '24px', padding: '10px 16px', fontSize: '13px', outline: 'none', transition: 'border-color .2s' }}
                  onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                  onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
                />
                <button type="submit" disabled={posting || !comment.trim()} style={{ background: comment.trim() ? 'var(--blue)' : 'var(--border)', color: comment.trim() ? '#fff' : 'var(--muted)', border: 'none', padding: '10px 18px', borderRadius: '24px', fontSize: '13px', fontWeight: 600, cursor: comment.trim() ? 'pointer' : 'default', transition: 'all .2s' }}>
                  {posting ? '...' : 'Envoyer'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
